export type Rgb = readonly [number, number, number];

const HEX = /^#?([0-9a-f]{6})$/i;

export function isHexColor(value: string | null | undefined): value is string {
  return typeof value === "string" && HEX.test(value.trim());
}

export function hexToRgb(hex: string): Rgb {
  const match = HEX.exec(hex.trim());
  if (!match?.[1]) throw new Error(`Not a hex color: ${hex}`);
  const n = parseInt(match[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbToHex([r, g, b]: Rgb): string {
  return `#${[r, g, b].map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, "0")).join("")}`;
}

/** WCAG 2.x relative luminance. */
export function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio, 1 to 21. */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/** Linear mix in sRGB: t=0 gives `from`, t=1 gives `to`. */
export function mix(from: string, to: string, t: number): string {
  const a = hexToRgb(from);
  const b = hexToRgb(to);
  return rgbToHex([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]);
}

/**
 * Move `color` toward black or white until it reaches `minRatio` against `background`.
 * Returns the color unchanged when it already passes.
 */
export function ensureContrast(color: string, background: string, minRatio: number, toward: "darker" | "lighter"): string {
  const target = toward === "darker" ? "#000000" : "#ffffff";
  let current = color;
  for (let step = 1; step <= 40 && contrastRatio(current, background) < minRatio; step += 1) {
    current = mix(color, target, step / 40);
  }
  return current;
}

export function bestTextOn(background: string, light = "#ffffff", dark = "#0b1220"): string {
  return contrastRatio(light, background) >= contrastRatio(dark, background) ? light : dark;
}
